import {
  HasuraDataTableColumnDef,
  DataTableEx,
  DataTableExProps,
  DataTableExRef,
} from '@kirz/mui-admin';
import { Chip, Tooltip, Typography } from '@mui/material';
import React, {
  forwardRef,
  Ref,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

const STATUS_CONFIG = {
  queued: { label: 'В очереди', color: 'default' as const },
  running: { label: 'Выполняется', color: 'info' as const },
  completed: { label: 'Завершено', color: 'success' as const },
  failed: { label: 'Ошибка', color: 'error' as const },
};

const TYPE_CONFIG = {
  video: { label: 'Видео', color: 'primary' as const },
  image: { label: 'Изображение', color: 'secondary' as const },
};

export const GenerationsTable = forwardRef(
  (
    props: Partial<DataTableExProps> & {
      id: string;
      filterColumns?: (
        columns: HasuraDataTableColumnDef[],
      ) => HasuraDataTableColumnDef[];
    },
    ref: Ref<DataTableExRef>,
  ) => {
    const { filterColumns, ...rest } = props;
    const tableRef = useRef<DataTableExRef>(null);

    useImperativeHandle(ref, () => tableRef.current!, []);

    const columns = useMemo<HasuraDataTableColumnDef[]>(
      () =>
        (filterColumns || ((x: HasuraDataTableColumnDef[]) => x))([
          {
            field: 'clientId',
            headerName: 'Клиент',
            width: 100,
          },
          {
            field: 'input',
            headerName: 'Ввод',
            type: 'file',
            hideText: true,
            selector: 'inputs { file { id contentType name extension } }',
            width: 100,
            sortable: false,
            valueGetter({ row }) {
              return row.inputs?.[0]?.file?.id;
            },
            fetchMetadata(row) {
              if (!row.inputs?.[0]?.file) {
                return null;
              }

              return {
                ...row.inputs?.[0]?.file,
                thumbnail: `/api/v1/files/w_100,h_100/${row.inputs?.[0]?.file.id}`,
              };
            },
          },
          {
            field: 'result',
            headerName: 'Результат',
            type: 'file',
            hideText: true,
            selector: 'result { id contentType name extension }',
            width: 100,
            sortable: false,
            valueGetter({ row }) {
              return row.result?.id;
            },
            fetchMetadata(row) {
              if (!row.result) {
                return null;
              }

              return {
                ...row.result,
                thumbnail: `/api/v1/files/w_100,h_100/${row.result.id}`,
              };
            },
          },
          {
            field: 'template { *name* model ratio }',
            headerName: 'Шаблон',
            flex: 1,
            type: 'relationship',
            valueGetter({ row }) {
              return row.template?.name || '-';
            },
          },
          {
            field: 'template { model ratio }',
            headerName: 'Параметры',
            width: 150,
            type: 'relationship',
            renderCell({ row }) {
              return (
                <Typography variant="body2">
                  {row.template?.model}
                  <br />
                  {row.template?.ratio}
                </Typography>
              );
            },
          },
          {
            field: 'type',
            headerName: 'Тип',
            width: 120,
            renderCell({ row }) {
              const config = TYPE_CONFIG[row.type as keyof typeof TYPE_CONFIG];
              if (!config) return row.type;
              return (
                <Chip label={config.label} color={config.color} size="small" />
              );
            },
          },
          {
            field: 'status',
            headerName: 'Статус',
            width: 130,
            renderCell({ row }) {
              const config =
                STATUS_CONFIG[row.status as keyof typeof STATUS_CONFIG];
              if (!config) return row.status;

              return (
                <Chip label={config.label} color={config.color} size="small" />
              );
            },
          },
          {
            field: 'prompt',
            headerName: 'Промпт',
            flex: 1,
            selector: 'prompt errorText',
            renderCell({ row }) {
              if (row.errorText) {
                return (
                  <Tooltip title={row.errorText} placement="top">
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'help',
                      }}
                    >
                      {row.errorText}
                    </Typography>
                  </Tooltip>
                );
              }

              if (!row.prompt) return '-';

              return (
                <Tooltip title={row.prompt} placement="top">
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'help',
                    }}
                  >
                    {row.prompt}
                  </Typography>
                </Tooltip>
              );
            },
          },
          {
            field: 'createdAt',
            headerName: 'Дата',
            type: 'date',
            width: 100,
          },
        ]),
      [filterColumns],
    );

    return (
      <DataTableEx
        {...rest}
        ref={tableRef}
        source="generation"
        columns={columns}
        editable={false}
        persistStateMode="query"
        sortBy={rest.sortBy ?? { field: 'createdAt', sort: 'desc' }}
      />
    );
  },
);
